import ContentHeader from "@/components/Bundle/Collection/CollectionPage/ContentHeader";
import CollectionPageHeader from "@/components/Bundle/Collection/CollectionPage/Header";
import MyComment from "@/components/Comment";
import Content from "@/components/Explore/Pubmed/Content";
import { useGetCollection } from "@/hooks/useCollection";
import { useLazyCollectorMetaInfos } from "@/hooks/useMeta";
import useTranslation from "@/hooks/useTranslation";
import { DisplayPreference } from "@/interfaces/pubmed.interface";
import CommonLayout from "@/layouts/Common";
import {
  collectionState,
  showCollectorCommentsState,
} from "@/stores/collection";
import { pubmedMergeFieldState } from "@/stores/pubmed";
import { firstCapitalize, restoreUUID } from "@/utils/string.util";
import { Divider, Spin } from "antd";
import { NextPage } from "next";
import Head from "next/head";
import React, { useEffect, useMemo } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import styles from "./styles.module.less";

interface IProps {
  page: number;
  size: number;
  id: string;
}

const CollectionPage: NextPage<IProps> = (props) => {
  const { page, size, id } = props;
  const { loading, handleChangePage, refetch, gLoading } = useGetCollection(
    props
  );

  const collection = useRecoilValue(collectionState);

  const fetchResults = useMemo(() => {
    return collection?.papers?.map((paper) => ({
      ...paper.content,
      journal: paper.journal,
    }));
  }, [collection]);

  const { t } = useTranslation("bundle");

  const [displayPreference, setDisplayPreference] = useRecoilState<
    Partial<DisplayPreference>
  >(pubmedMergeFieldState("displayPreference"));

  const setShowCollectorComment = useSetRecoilState(showCollectorCommentsState);

  const { getMeta } = useLazyCollectorMetaInfos(
    collection?.papers?.map((paper) => paper?.id),
    collection?.owner?.id
  );

  useEffect(() => {
    setShowCollectorComment({ show: true, userId: collection?.owner?.id });
    return () => {
      setShowCollectorComment({ show: false, userId: "" });
    };
  }, [collection]);

  useEffect(() => {
    getMeta();
  }, [collection]);

  return (
    <CommonLayout>
      <Head>
        <title>
          {firstCapitalize(collection?.name)} - {t("menu:bundle.collection")} -{" "}
          {t("menu:bundle.itself")} - MedicGo
        </title>
      </Head>
      <div className={styles.container}>
        <Spin spinning={gLoading}>
          <div className={styles.box}>
            {" "}
            <CollectionPageHeader
              username={collection?.owner?.username}
              id={collection?.id}
              _public={collection?.public}
              updated_at={collection?.updated_at}
              name={collection?.name}
              description={collection?.description}
              size={collection?.size}
            />
          </div>
          <div className={styles.box1}>
            <Divider orientation="left" className={styles.divider}>
              {t("collection.page.papers")}
            </Divider>
            <div className={styles.comments}>
              <Content
                locationInfo={{ page, size }}
                fetchResults={fetchResults}
                loadingFetch={loading}
                count={collection?.size}
                handleFetch={handleChangePage}
                Footer={false}
                Header={
                  <ContentHeader
                    pmids={fetchResults?.map((e) => e.ePmid)}
                    collectionId={id}
                    refetch={refetch}
                    verbose={displayPreference?.verbose}
                  />
                }
                compact
                verbose={displayPreference?.verbose}
              />
            </div>
          </div>
          <div className={styles.box2}>
            <Divider orientation="left" className={styles.divider}>
              {t("common:comment.itself")}
            </Divider>
            <div className={styles.comments}>
              <MyComment fatherId={collection?.id} />
            </div>
          </div>
        </Spin>
      </div>
    </CommonLayout>
  );
};
CollectionPage.getInitialProps = async (ctx) => {
  const page = parseInt((ctx.query.page as string) || "1", 10);
  const size = parseInt((ctx.query.size as string) || "10", 10);
  const id = restoreUUID(ctx.query.id as string);
  return {
    page,
    size,
    id,
    namespacesRequired: ["common", "menu", "bundle", "explore"],
  };
};

export default CollectionPage;
